import React, { useState } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Descriptions,
  Popconfirm,
  message,
  Empty,
  Spin,
  Tooltip,
  Form,
  Input,
  Select,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  HomeOutlined,
  BuildOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useTranslation } from '../hooks/useTranslation';
import { useCreateAddress, useUpdateAddress, useDeleteAddress, useSetDefaultAddress, useAdminCreateAddress, useAdminUpdateAddress, useAdminDeleteAddress, useAdminSetDefaultAddress } from '../hooks/useGraphQL';
import { useAuth } from '../hooks/useAuth';
import { HK_REGIONS_AND_DISTRICTS } from '../utils/hk-regions';
import dayjs from 'dayjs';
import { useEffect } from 'react';

const { Title, Text } = Typography;
const { Option } = Select;

interface Address {
  id: string;
  type: string;
  display_name: string;
  region: string;
  district: string;
  street_address: string;
  building_name?: string | null;
  floor?: string | null;
  block?: string | null;
  unit?: string | null;
  postal_code: string;
  override_address?: string | null;
  is_default: boolean;
  phone_number: string;
  contact_name: string;
  created_at: string;
  updated_at: string;
}

interface AddressManagementModalProps {
  visible: boolean;
  userId: string;
  userName: string;
  addresses: Address[];
  onCancel: () => void;
  onAddressesChange?: () => void;
}

// Helper function to get localized region name
const getLocalizedRegionName = (regionCode: string, locale: string = 'zh-HK'): string => {
  const region = HK_REGIONS_AND_DISTRICTS.find(r => r.code === regionCode);
  if (!region) return regionCode;

  switch (locale) {
    case 'en':
      return region.name_en;
    case 'zh-CN':
      return region.name_zh_cn;
    case 'zh-HK':
    default:
      return region.name_zh_hk;
  }
};

// Helper function to get localized district name
const getLocalizedDistrictName = (regionCode: string, districtCode: string, locale: string = 'zh-HK'): string => {
  const region = HK_REGIONS_AND_DISTRICTS.find(r => r.code === regionCode);
  if (!region) return districtCode;

  const district = region.districts.find(d => d.code === districtCode);
  if (!district) return districtCode;

  switch (locale) {
    case 'en':
      return district.name_en;
    case 'zh-CN':
      return district.name_zh_cn;
    case 'zh-HK':
    default:
      return district.name_zh_hk;
  }
};

// Helper function to format address
const formatAddress = (address: Address, t: (k: string, options?: { value: string }) => string, locale: string = 'zh-HK'): string => {
  if (address.override_address) {
    return address.override_address;
  }

  const localizedRegion = getLocalizedRegionName(address.region, locale);
  const localizedDistrict = getLocalizedDistrictName(address.region, address.district, locale);

  const parts = [
    localizedRegion,
    localizedDistrict,
    address.street_address,
    address.building_name,
    address.block && `${t('addresses.value.block', { value: address.block })}`,
    address.floor && `${t('addresses.value.floor', { value: address.floor })}`,
    address.unit && `${t('addresses.value.unit', { value: address.unit })}`,
    address.postal_code
  ].filter(Boolean);

  return parts.join(', ');
};

// Helper function to get address type icon
const getAddressTypeIcon = (type: string) => {
  switch (type) {
    case 'primary':
      return <HomeOutlined />;
    case 'office':
    case 'work':
      return <BuildOutlined />;
    default:
      return <EnvironmentOutlined />;
  }
};

// Helper function to get address type color
const getAddressTypeColor = (type: string) => {
  switch (type) {
    case 'primary':
      return 'green';
    case 'office':
    case 'work':
      return 'blue';
    default:
      return 'default';
  }
};

const AddressManagementModal: React.FC<AddressManagementModalProps> = ({
  visible,
  userId,
  userName,
  addresses,
  onCancel,
  onAddressesChange
}) => {
  const { t, currentLanguage } = useTranslation();
  const { user: currentUser } = useAuth();
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [localAddresses, setLocalAddresses] = useState<Address[]>(addresses);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Check if current user is admin
  const isAdmin = currentUser && (currentUser.is_super_admin || currentUser.permissions?.includes('update_users'));

  // GraphQL hooks - use admin mutations if user is admin, otherwise use regular mutations
  const [createAddress, { loading: createLoading }] = useCreateAddress();
  const [adminCreateAddress, { loading: adminCreateLoading }] = useAdminCreateAddress();
  const [updateAddress, { loading: updateLoading }] = useUpdateAddress();
  const [adminUpdateAddress, { loading: adminUpdateLoading }] = useAdminUpdateAddress();
  const [deleteAddress, { loading: deleteLoading }] = useDeleteAddress();
  const [adminDeleteAddress, { loading: adminDeleteLoading }] = useAdminDeleteAddress();
  const [setDefaultAddress, { loading: setDefaultLoading }] = useSetDefaultAddress();
  const [adminSetDefaultAddress, { loading: adminSetDefaultLoading }] = useAdminSetDefaultAddress();

  const isLoading = createLoading || adminCreateLoading || updateLoading || adminUpdateLoading || deleteLoading || adminDeleteLoading || setDefaultLoading || adminSetDefaultLoading;

  // Sync local addresses with prop addresses
  useEffect(() => {
    setLocalAddresses(addresses);
  }, [addresses]);

  // Populate edit form when editingAddress changes
  useEffect(() => {
    if (editingAddress) {
      editForm.setFieldsValue({
        type: editingAddress.type,
        display_name: editingAddress.display_name,
        region: editingAddress.region,
        district: editingAddress.district,
        street_address: editingAddress.street_address,
        building_name: editingAddress.building_name,
        floor: editingAddress.floor,
        block: editingAddress.block,
        unit: editingAddress.unit,
        postal_code: editingAddress.postal_code,
        phone_number: editingAddress.phone_number,
        contact_name: editingAddress.contact_name,
        is_default: editingAddress.is_default
      });
    }
  }, [editingAddress, editForm]);


  const handleSetDefault = async (addressId: string) => {
    try {
      // Optimistically update local state
      setLocalAddresses(prev => 
        prev.map(addr => ({
          ...addr,
          is_default: addr.id === addressId
        }))
      );

      if (isAdmin) {
        await adminSetDefaultAddress({
          variables: { addressId }
        });
      } else {
        await setDefaultAddress({
          variables: { addressId }
        });
      }
      message.success(t('addresses.defaultAddressSet'));
      onAddressesChange?.();
    } catch (error) {
      console.error('Error setting default address:', error);
      message.error('Failed to set default address');
      // Revert optimistic update on error
      setLocalAddresses(addresses);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      // Optimistically update local state
      setLocalAddresses(prev => prev.filter(addr => addr.id !== addressId));

      if (isAdmin) {
        await adminDeleteAddress({
          variables: { id: addressId }
        });
      } else {
        await deleteAddress({
          variables: { id: addressId }
        });
      }
      message.success(t('addresses.addressDeleted'));
      onAddressesChange?.();
    } catch (error) {
      console.error('Error deleting address:', error);
      message.error('Failed to delete address');
      // Revert optimistic update on error
      setLocalAddresses(addresses);
    }
  };

  const handleCreateAddress = async (values: any) => {
    try {
      const createAddressDto = {
        app_user_id: userId,
        type: values.type,
        display_name: values.display_name,
        region: values.region,
        district: values.district,
        street_address: values.street_address,
        building_name: values.building_name,
        floor: values.floor,
        block: values.block,
        unit: values.unit,
        postal_code: values.postal_code || '000000',
        phone_number: values.phone_number,
        contact_name: values.contact_name,
        is_default: values.is_default || false,
        tenant: 'default'
      };

      // Create optimistic address object
      const optimisticAddress: Address = {
        id: `temp-${Date.now()}`, // Temporary ID
        type: values.type,
        display_name: values.display_name,
        region: values.region,
        district: values.district,
        street_address: values.street_address,
        building_name: values.building_name,
        floor: values.floor,
        block: values.block,
        unit: values.unit,
        postal_code: values.postal_code || '000000',
        phone_number: values.phone_number,
        contact_name: values.contact_name,
        is_default: values.is_default || false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Optimistically update local state
      setLocalAddresses(prev => {
        // If setting as default, unset other defaults
        if (values.is_default) {
          return [optimisticAddress, ...prev.map(addr => ({ ...addr, is_default: false }))];
        }
        return [...prev, optimisticAddress];
      });

      let result;
      if (isAdmin) {
        result = await adminCreateAddress({
          variables: { createAddressDto }
        });
      } else {
        result = await createAddress({
          variables: { createAddressDto }
        });
      }
      
      message.success(t('addresses.addressCreated'));
      setShowAddForm(false);
      form.resetFields();
      onAddressesChange?.();
    } catch (error) {
      console.error('Error creating address:', error);
      message.error('Failed to create address');
      // Revert optimistic update on error
      setLocalAddresses(addresses);
    }
  };

  const handleUpdateAddress = async (values: any) => {
    if (!editingAddress) return;

    try {
      const updateAddressDto = {
        type: values.type,
        display_name: values.display_name,
        region: values.region,
        district: values.district,
        street_address: values.street_address,
        building_name: values.building_name || null,
        floor: values.floor || null,
        block: values.block || null,
        unit: values.unit || null,
        postal_code: values.postal_code || '000000',
        phone_number: values.phone_number,
        contact_name: values.contact_name,
        is_default: values.is_default || false
      };

      // Optimistically update local state
      setLocalAddresses(prev => {
        const updated = prev.map(addr => {
          if (addr.id === editingAddress.id) {
            return {
              ...addr,
              ...updateAddressDto,
              updated_at: new Date().toISOString()
            };
          }
          // If setting as default, unset other defaults
          if (values.is_default && addr.id !== editingAddress.id) {
            return { ...addr, is_default: false };
          }
          return addr;
        });
        return updated;
      });

      if (isAdmin) {
        await adminUpdateAddress({
          variables: { 
            id: editingAddress.id,
            updateAddressDto 
          }
        });
      } else {
        await updateAddress({
          variables: { 
            id: editingAddress.id,
            updateAddressDto 
          }
        });
      }
      
      message.success(t('addresses.addressUpdated') || 'Address updated successfully');
      setEditingAddress(null);
      editForm.resetFields();
      onAddressesChange?.();
    } catch (error) {
      console.error('Error updating address:', error);
      message.error('Failed to update address');
      // Revert optimistic update on error
      setLocalAddresses(addresses);
    }
  };

  const columns = [
    {
      title: t('addresses.addressDetails'),
      key: 'address',
      render: (address: Address) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            {getAddressTypeIcon(address.type)}
            <Text strong style={{ marginLeft: 8 }}>
              {address.display_name}
            </Text>
            {address.is_default && (
              <Tag color="gold" icon={<StarFilled />} style={{ marginLeft: 8 }}>
                {t('addresses.default')}
              </Tag>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {formatAddress(address, t, currentLanguage)}
          </Text>
        </div>
      ),
    },
    {
      title: t('addresses.contactName'),
      key: 'contact',
      render: (address: Address) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <UserOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{address.contact_name}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
            <Text style={{ fontSize: '12px' }}>{address.phone_number}</Text>
          </div>
        </div>
      ),
    },
    {
      title: t('addresses.addressType'),
      key: 'type',
      render: (address: Address) => (
        <Tag color={getAddressTypeColor(address.type)} icon={getAddressTypeIcon(address.type)}>
          {address.type}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (address: Address) => (
        <Space>
          {!address.is_default && (
            <Tooltip title={t('addresses.setAsDefault')}>
              <Button
                type="text"
                icon={<StarOutlined />}
                onClick={() => handleSetDefault(address.id)}
                loading={setDefaultLoading}
              />
            </Tooltip>
          )}
          <Tooltip title={t('addresses.editAddress')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setShowAddForm(false);
                form.resetFields();
                setEditingAddress(address);
              }}
            />
          </Tooltip>
          <Popconfirm
            title={t('addresses.confirmDeleteAddress')}
            onConfirm={() => handleDeleteAddress(address.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Tooltip title={t('addresses.deleteAddress')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoading}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {t('addresses.manageAddresses')} - {userName}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel} icon={<CloseOutlined />}>
          {t('common.close')}
        </Button>
      ]}
      width={1000}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAddress(null);
              editForm.resetFields();
              setShowAddForm(true);
            }}
            disabled={isLoading}
          >
            {t('addresses.addAddress')}
          </Button>
        </div>


        {localAddresses.length === 0 && !showAddForm ? (
          <Empty
            description={t('addresses.noAddressesFound')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button 
              type="primary" 
              onClick={() => {
                setEditingAddress(null);
                editForm.resetFields();
                setShowAddForm(true);
              }}
            >
              {t('addresses.addFirstAddress')}
            </Button>
          </Empty>
        ) : (
          <>
            {localAddresses.length > 0 && (
              <Table
                columns={columns}
                dataSource={localAddresses}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
              />
            )}

            {showAddForm && (
              <Card 
                title={t('addresses.addAddress')} 
                style={{ marginTop: 16 }}
                extra={
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => {
                      setShowAddForm(false);
                      form.resetFields();
                    }}
                  />
                }
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleCreateAddress}
                  initialValues={{
                    type: 'primary',
                    is_default: false
                  }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.displayName')}
                        name="display_name"
                        rules={[{ required: true, message: t('addresses.pleaseInputDisplayName') }]}
                      >
                        <Input placeholder="e.g., Home, Office" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.addressType')}
                        name="type"
                        rules={[{ required: true, message: t('addresses.pleaseSelectAddressType') }]}
                      >
                        <Select>
                          <Option value="primary">{t('addresses.type.primary')}</Option>
                          <Option value="office">{t('addresses.type.office')}</Option>
                          <Option value="other">{t('addresses.otherAddress')}</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.contactName')}
                        name="contact_name"
                        rules={[{ required: true, message: t('addresses.pleaseInputContactName') }]}
                      >
                        <Input placeholder="Contact person name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.phoneNumber')}
                        name="phone_number"
                        rules={[{ required: true, message: t('addresses.pleaseInputPhoneNumber') }]}
                      >
                        <Input placeholder="+852-12345678" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.region')}
                        name="region"
                        rules={[{ required: true, message: t('addresses.pleaseSelectRegion') }]}
                      >
                        <Select placeholder="Select region">
                          {HK_REGIONS_AND_DISTRICTS.map(region => (
                            <Option key={region.code} value={region.code}>
                              {currentLanguage === 'en' ? region.name_en : region.name_zh_hk}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.district')}
                        name="district"
                        rules={[{ required: true, message: t('addresses.pleaseSelectDistrict') }]}
                      >
                        <Select placeholder="Select district">
                          {HK_REGIONS_AND_DISTRICTS.map(region => 
                            region.districts.map(district => (
                              <Option key={district.code} value={district.code}>
                                {currentLanguage === 'en' ? district.name_en : district.name_zh_hk}
                              </Option>
                            ))
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={t('addresses.streetAddress')}
                    name="street_address"
                    rules={[{ required: true, message: t('addresses.pleaseInputStreetAddress') }]}
                  >
                    <Input placeholder="Street address" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.buildingName')}
                        name="building_name"
                      >
                        <Input placeholder="Building name" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.block')}
                        name="block"
                      >
                        <Input placeholder="Block" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.floor')}
                        name="floor"
                      >
                        <Input placeholder="Floor" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.unit')}
                        name="unit"
                      >
                        <Input placeholder="Unit" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.postalCode')}
                        name="postal_code"
                      >
                        <Input placeholder="e.g., 00000 (defaults to 000000 if empty)" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.isDefault')}
                        name="is_default"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ textAlign: 'right', marginTop: 24 }}>
                    <Space>
                      <Button 
                        onClick={() => {
                          setShowAddForm(false);
                          form.resetFields();
                        }}
                        disabled={createLoading || adminCreateLoading}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={createLoading || adminCreateLoading}
                        icon={<PlusOutlined />}
                      >
                        {t('addresses.addAddress')}
                      </Button>
                    </Space>
                  </div>
                </Form>
              </Card>
            )}

            {editingAddress && (
              <Card 
                title={t('addresses.editAddress') || 'Edit Address'} 
                style={{ marginTop: 16 }}
                extra={
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => {
                      setEditingAddress(null);
                      editForm.resetFields();
                    }}
                  />
                }
              >
                <Form
                  form={editForm}
                  layout="vertical"
                  onFinish={handleUpdateAddress}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.displayName')}
                        name="display_name"
                        rules={[{ required: true, message: t('addresses.pleaseInputDisplayName') }]}
                      >
                        <Input placeholder="e.g., Home, Office" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.addressType')}
                        name="type"
                        rules={[{ required: true, message: t('addresses.pleaseSelectAddressType') }]}
                      >
                        <Select>
                          <Option value="primary">{t('addresses.type.primary')}</Option>
                          <Option value="office">{t('addresses.type.office')}</Option>
                          <Option value="other">{t('addresses.otherAddress')}</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.contactName')}
                        name="contact_name"
                        rules={[{ required: true, message: t('addresses.pleaseInputContactName') }]}
                      >
                        <Input placeholder="Contact person name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.phoneNumber')}
                        name="phone_number"
                        rules={[{ required: true, message: t('addresses.pleaseInputPhoneNumber') }]}
                      >
                        <Input placeholder="+852-12345678" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.region')}
                        name="region"
                        rules={[{ required: true, message: t('addresses.pleaseSelectRegion') }]}
                      >
                        <Select placeholder="Select region">
                          {HK_REGIONS_AND_DISTRICTS.map(region => (
                            <Option key={region.code} value={region.code}>
                              {currentLanguage === 'en' ? region.name_en : region.name_zh_hk}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.district')}
                        name="district"
                        rules={[{ required: true, message: t('addresses.pleaseSelectDistrict') }]}
                      >
                        <Select placeholder="Select district">
                          {HK_REGIONS_AND_DISTRICTS.map(region => 
                            region.districts.map(district => (
                              <Option key={district.code} value={district.code}>
                                {currentLanguage === 'en' ? district.name_en : district.name_zh_hk}
                              </Option>
                            ))
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label={t('addresses.streetAddress')}
                    name="street_address"
                    rules={[{ required: true, message: t('addresses.pleaseInputStreetAddress') }]}
                  >
                    <Input placeholder="Street address" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.buildingName')}
                        name="building_name"
                      >
                        <Input placeholder="Building name" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.block')}
                        name="block"
                      >
                        <Input placeholder="Block" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.floor')}
                        name="floor"
                      >
                        <Input placeholder="Floor" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label={t('addresses.unit')}
                        name="unit"
                      >
                        <Input placeholder="Unit" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.postalCode')}
                        name="postal_code"
                      >
                        <Input placeholder="e.g., 00000 (defaults to 000000 if empty)" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label={t('addresses.isDefault')}
                        name="is_default"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ textAlign: 'right', marginTop: 24 }}>
                    <Space>
                      <Button 
                        onClick={() => {
                          setEditingAddress(null);
                          editForm.resetFields();
                        }}
                        disabled={updateLoading || adminUpdateLoading}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={updateLoading || adminUpdateLoading}
                        icon={<EditOutlined />}
                      >
                        {t('common.save') || 'Save'}
                      </Button>
                    </Space>
                  </div>
                </Form>
              </Card>
            )}
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default AddressManagementModal;
